/**
 * Date min/max validation for validationRules.minDate / maxDate / minDateFrom.
 *
 * Mirrors Portal-Backend/services/dateRangeValidation.js, with ONE deliberate
 * difference: "today" here is the browser's local date — the applicant's
 * actual today, which is also where the date picker stops.
 *
 * The server does not know the applicant's timezone, so it accepts every
 * date that is "today" somewhere on Earth (its todayRange). It is therefore
 * always at least as permissive as this file. Do not "sync" either side to a
 * single fixed today: that is what made the N-400 address history
 * impossible to save during U.S. evenings.
 */

import { ValidationRules } from "../types/form";

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const MIN_DATE_FROM_MESSAGE =
  "This date cannot be before your date of birth.";

export function formatLocalIsoDate(date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function resolveDateBound(
  bound: string | undefined,
  referenceDate = new Date(),
): string | null {
  if (!bound) return null;
  const trimmed = bound.trim();
  if (trimmed === "today") return formatLocalIsoDate(referenceDate);
  if (ISO_DATE.test(trimmed)) return trimmed;
  return null;
}

function getAnswerByQuestionId(
  answers: Record<string, unknown> | undefined,
  questionId: number,
): unknown {
  if (!answers) return undefined;
  return (
    answers[`q_${questionId}`] ??
    answers[questionId] ??
    answers[String(questionId)]
  );
}

/** Resolve minDateFrom to an ISO date, or null when the source answer is missing/invalid. */
export function resolveMinDateFromAnswers(
  rules: Pick<ValidationRules, "minDateFrom"> | null | undefined,
  answers?: Record<string, unknown>,
): string | null {
  const sourceId = Number(rules?.minDateFrom?.questionId);
  if (!Number.isInteger(sourceId) || sourceId <= 0) return null;
  const value = String(getAnswerByQuestionId(answers, sourceId) ?? "").trim();
  if (!ISO_DATE.test(value)) return null;
  return value;
}

export type DateRangeValidationResult =
  | { ok: true; normalized: string }
  | { ok: false; message: string };

export function validateDateRange(
  raw: unknown,
  rules: Pick<ValidationRules, "minDate" | "maxDate" | "minDateFrom">,
  referenceDate = new Date(),
  answers?: Record<string, unknown>,
): DateRangeValidationResult {
  const value = String(raw ?? "").trim();
  if (!ISO_DATE.test(value)) {
    return { ok: false, message: "Enter a valid date." };
  }

  const minFrom = resolveMinDateFromAnswers(rules, answers);
  if (minFrom && value < minFrom) {
    return { ok: false, message: MIN_DATE_FROM_MESSAGE };
  }

  const min = resolveDateBound(
    typeof rules.minDate === "string" ? rules.minDate : undefined,
    referenceDate,
  );
  const max = resolveDateBound(
    typeof rules.maxDate === "string" ? rules.maxDate : undefined,
    referenceDate,
  );

  if (min && value < min) {
    return { ok: false, message: "Date is too early for this field." };
  }
  if (max && value > max) {
    if (rules.maxDate === "today") {
      return { ok: false, message: "Date cannot be in the future." };
    }
    return { ok: false, message: "Date is too late for this field." };
  }

  return { ok: true, normalized: value };
}

export function hasDateBounds(rules: ValidationRules | null): boolean {
  if (!rules) return false;
  return Boolean(
    rules.minDate ||
      rules.maxDate ||
      Number(rules.minDateFrom?.questionId) > 0,
  );
}
